<script setup>
// Aviso obligatorio "Un solo equipo" · modal bloqueante (Victor 2026-06-07, P1·A).
// Se muestra a TODOS los agentes (no super_admin) la 1ra vez que entran cada día
// hasta la fecha de vigencia (10 jun). Bloquea TODO el sistema: no hay X ni Esc,
// la única acción es Confirmado. Al confirmar se registra (server) y se quita por
// ese día; al día siguiente vuelve a salir. La decisión de mostrar la toma el
// backend (#status) para que sea por agente y no por navegador.
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import AnnouncementAPI from 'dashboard/api/announcement';

const store = useStore();
const visible = ref(false);
const saving = ref(false);
let ran = false;

// Confirmar es OBLIGATORIO (Victor 2026-06-09): si dan clic en cualquier lugar
// que NO sea el botón, este VIBRA, aparece una flecha y un aviso rojo (mismo tema
// que el 911 de atrasos). El botón además parpadea/pulsa para llamar el clic.
const nagging = ref(false);
const shaking = ref(false);
let nagTimer = null;
function enforce() {
  if (saving.value) return;
  nagging.value = true;
  shaking.value = false;
  requestAnimationFrame(() => {
    shaking.value = true;
  });
  clearTimeout(nagTimer);
  nagTimer = setTimeout(() => {
    nagging.value = false;
    shaking.value = false;
  }, 2600);
}

const currentAccount = computed(() => store.getters.getCurrentAccount || null);
const isSuperAdmin = computed(() => currentAccount.value?.super_admin === true);

async function maybeShow() {
  if (isSuperAdmin.value) return; // Victor / super admin no ven el aviso
  try {
    const { data } = await AnnouncementAPI.status();
    if (data?.pending) visible.value = true;
  } catch (e) {
    /* silencioso */
  }
}

async function confirmar() {
  if (saving.value) return;
  saving.value = true;
  try {
    await AnnouncementAPI.confirm();
    visible.value = false;
  } catch (e) {
    saving.value = false;
  }
}

watch(
  currentAccount,
  acc => {
    if (acc && acc.id && !ran) {
      ran = true;
      maybeShow();
    }
  },
  { immediate: true }
);
</script>

<template>
  <div v-if="visible" class="an-overlay" @click="enforce">
    <div class="an-lock">🔒 El sistema está bloqueado hasta confirmar</div>
    <div class="an-toast" :class="{ show: nagging }">
      ⚠️ <b>Obligatorio:</b> debes dar clic en “Confirmado” para usar el sistema.
    </div>
    <div class="an-card">
      <div class="an-crest">❖</div>
      <div class="an-kick">Aviso importante</div>
      <h2 class="an-title">
        Nueva actualización: <b>Un solo equipo</b>
      </h2>
      <span class="an-vig">Vigencia <b>· 17 de junio</b></span>

      <ul class="an-pts">
        <li>
          <span class="an-dot" />
          <span>Entran y <b>atienden clientes de las 4 sucursales</b>.</span>
        </li>
        <li>
          <span class="an-dot" />
          <span><b>Puedes vender</b> para cualquier sucursal.</span>
        </li>
        <li>
          <span class="an-dot" />
          <span>Solo podrás atender a <b>tus clientes asignados</b>.</span>
        </li>
        <li>
          <span class="an-dot" />
          <span><b>Precios y políticas iguales</b> para todas.</span>
        </li>
        <li>
          <span class="an-dot" />
          <span>
            Si tienes duda de un <b>plan, vestido o precio</b>, investiga primero
            con la sucursal correspondiente. <b>No des información que no manejes.</b>
          </span>
        </li>
      </ul>

      <span class="an-tagline">Una empresa. Cuatro sucursales. Un solo equipo.</span>

      <div class="an-arrow" :class="{ show: nagging }">
        Debes confirmar aquí para continuar ↓
      </div>
      <button
        type="button"
        class="an-btn"
        :class="{ saving, shake: shaking }"
        :disabled="saving"
        @click.stop="confirmar"
      >
        <span class="an-blink">✅</span> Confirmado
      </button>

      <div class="an-foot">
        Confirmar es <span class="an-lk">obligatorio</span>. Hasta dar clic no
        podrás usar el sistema, y el aviso reaparecerá <b>una vez al día</b> hasta
        el 17 de junio. · Se registra con fecha y hora.
      </div>
    </div>
  </div>
</template>

<style scoped>
.an-overlay {
  position: fixed;
  inset: 0;
  z-index: 9995;
  background: radial-gradient(
    900px 600px at 50% 42%,
    rgba(0, 0, 0, 0.5),
    rgba(0, 0, 0, 0.84)
  );
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.an-lock {
  position: absolute;
  top: 18px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 10px;
  font-weight: 300;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #e0a23c;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: 1px solid rgba(224, 162, 60, 0.3);
  background: rgba(224, 162, 60, 0.06);
  padding: 6px 14px;
  border-radius: 999px;
}
.an-card {
  width: 100%;
  max-width: 452px;
  background: linear-gradient(180deg, rgba(20, 20, 23, 0.95), rgba(11, 11, 13, 0.98));
  border: 1px solid rgba(201, 163, 90, 0.26);
  border-radius: 20px;
  backdrop-filter: blur(22px);
  box-shadow: 0 55px 140px rgba(0, 0, 0, 0.75),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
  padding: 34px 36px 28px;
  text-align: center;
}
.an-crest {
  width: 44px;
  height: 44px;
  margin: 0 auto 18px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  border: 1px solid rgba(201, 163, 90, 0.26);
  background: radial-gradient(
    circle at 40% 30%,
    rgba(201, 163, 90, 0.18),
    rgba(201, 163, 90, 0.02)
  );
  color: #c9a35a;
  font-size: 17px;
}
.an-kick {
  font-size: 9.5px;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: #c9a35a;
  font-weight: 600;
  margin-bottom: 12px;
}
.an-title {
  font-size: 20px;
  font-weight: 400;
  line-height: 1.3;
  margin-bottom: 14px;
  color: #f4f4f5;
}
.an-title b {
  font-weight: 600;
}
.an-vig {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 9.5px;
  letter-spacing: 0.06em;
  color: #9a9a9f;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(255, 255, 255, 0.02);
  padding: 4px 11px;
  border-radius: 999px;
}
.an-vig b {
  color: #c9a35a;
  font-weight: 600;
}
.an-pts {
  list-style: none;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 11px;
  margin: 20px 0;
  padding: 0;
}
.an-pts li {
  display: flex;
  gap: 12px;
  font-size: 12px;
  font-weight: 300;
  line-height: 1.5;
  color: #f4f4f5;
}
.an-pts li b {
  font-weight: 600;
}
.an-dot {
  flex: 0 0 auto;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #c9a35a;
  margin-top: 7px;
  box-shadow: 0 0 8px rgba(201, 163, 90, 0.5);
}
.an-tagline {
  display: block;
  font-size: 10.5px;
  font-weight: 300;
  letter-spacing: 0.04em;
  color: #636367;
  font-style: italic;
  margin-bottom: 22px;
}
.an-btn {
  width: 100%;
  border: 0;
  cursor: pointer;
  font-weight: 600;
  font-size: 12px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #1a1306;
  background: linear-gradient(180deg, #d8b56e, #bd9249);
  box-shadow: 0 12px 32px rgba(201, 163, 90, 0.22);
  padding: 15px;
  border-radius: 12px;
  transition: 0.18s;
  animation: an-pulse 1.5s ease-in-out infinite;
}
.an-btn:hover {
  filter: brightness(1.06);
  transform: translateY(-1px);
}
.an-btn.saving {
  opacity: 0.6;
  cursor: default;
  transform: none;
  animation: none;
}
@keyframes an-pulse {
  0%, 100% { box-shadow: 0 12px 32px rgba(201, 163, 90, 0.22); }
  50% { box-shadow: 0 0 28px 6px rgba(201, 163, 90, 0.55); }
}
.an-blink {
  display: inline-block;
  animation: an-blink 1s steps(2, start) infinite;
}
@keyframes an-blink {
  50% { opacity: 0.3; }
}
.an-btn.shake {
  animation: an-shake 0.45s, an-pulse 1.5s ease-in-out infinite;
}
@keyframes an-shake {
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-8px); }
  40% { transform: translateX(8px); }
  60% { transform: translateX(-6px); }
  80% { transform: translateX(6px); }
}
/* Flecha "Debes confirmar aquí" (obligatorio) */
.an-arrow {
  text-align: center;
  color: #e0564d;
  font-size: 11.5px;
  font-weight: 700;
  height: 0;
  opacity: 0;
  overflow: hidden;
  transition: opacity 0.25s, height 0.25s, margin 0.25s;
}
.an-arrow.show {
  height: 18px;
  opacity: 1;
  margin-bottom: 8px;
  animation: an-bob 1s infinite;
}
@keyframes an-bob {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(4px); }
}
/* Toast obligatorio arriba */
.an-toast {
  position: absolute;
  top: 54px;
  left: 50%;
  transform: translateX(-50%) translateY(-20px);
  z-index: 1;
  background: linear-gradient(180deg, #2a1b1a, #241615);
  border: 1px solid rgba(224, 86, 77, 0.45);
  color: #f1b8b3;
  font-size: 12px;
  font-weight: 600;
  padding: 10px 16px;
  border-radius: 11px;
  opacity: 0;
  transition: 0.3s;
  box-shadow: 0 14px 30px -12px #000;
  pointer-events: none;
  white-space: nowrap;
}
.an-toast.show {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}
.an-toast b { color: #fff; }
.an-foot {
  margin-top: 14px;
  font-size: 9.5px;
  font-weight: 300;
  color: #636367;
  line-height: 1.6;
  letter-spacing: 0.01em;
}
.an-lk {
  color: #e0a23c;
  font-weight: 500;
}
</style>
